import { useEffect, useMemo, useState } from "react";
import {
  HUBSPOT_PROPERTY_REGISTRY,
  SUBMISSION_DOWNSTREAM_KINDS,
  type FormDefinition,
  type FormWorkflowConfig,
  type HubSpotObject,
} from "@colab/shared";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useAutosave, type SaveStatus } from "@/hooks/use-autosave";
import { useUpdateFormWorkflow } from "./use-update-form-workflow";

// Workflow tab — wires submissions to a downstream object on acceptance:
//   • downstream_kind (deal / lead / partner_application / contact / company / none)
//   • auto_accept (used by the deal-registration form)
//   • mapping table (form-field → downstream-property)
//
// Owns a local mirror of the workflow shape so input typing stays
// responsive; 500ms debounced PATCH via useUpdateFormWorkflow. The mapping
// table's right-hand options swap based on downstream_kind — 'deal' uses
// the static public.deals column list, HubSpot kinds use the shared
// HUBSPOT_PROPERTY_REGISTRY.

export function WorkflowConfigForm({
  formId,
  workflow,
  definition,
  onSaveStatusChange,
}: {
  formId: string;
  workflow: FormWorkflowConfig;
  definition: FormDefinition;
  onSaveStatusChange?: (status: SaveStatus) => void;
}) {
  const mutation = useUpdateFormWorkflow();
  const [local, setLocal] = useState<FormWorkflowConfig>(workflow);

  // Re-hydrate when the server snapshot changes (e.g. after a fresh fetch
  // or a tab switch). The local mirror is read-only until the user types.
  useEffect(() => {
    setLocal(workflow);
  }, [workflow]);

  const autosave = useAutosave<FormWorkflowConfig>({
    onFlush: async (next) => {
      await mutation.mutateAsync({ id: formId, workflow: next });
    },
    wait: 500,
  });

  useEffect(() => {
    onSaveStatusChange?.(autosave.status);
  }, [autosave.status, onSaveStatusChange]);

  function patchAcceptance(patch: Partial<FormWorkflowConfig["acceptance"]>) {
    const next: FormWorkflowConfig = {
      ...local,
      acceptance: { ...local.acceptance, ...patch },
    };
    setLocal(next);
    autosave.queue(next);
  }

  const acceptance = local.acceptance;

  // Flatten the form definition into a list of mappable fields. Section
  // headings are skipped (they never emit a payload key).
  const mappableFields = useMemo(() => {
    const out: { name: string; label: string }[] = [];
    for (const section of definition.sections) {
      for (const field of section.fields) {
        if (field.kind === "section_heading") continue;
        out.push({ name: field.name, label: field.label });
      }
    }
    return out;
  }, [definition]);

  const downstreamOptions = useDownstreamPropertyOptions(acceptance.downstream_kind);

  function setMapping(formName: string, property: string | null) {
    const next = { ...acceptance.mapping };
    if (property === null || property === "") delete next[formName];
    else next[formName] = property;
    patchAcceptance({ mapping: next });
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border bg-card p-4">
          <Label htmlFor="downstream_kind">Downstream object</Label>
          <Select
            value={acceptance.downstream_kind}
            onValueChange={(v) =>
              patchAcceptance({
                downstream_kind: v as typeof acceptance.downstream_kind,
                // Clear the mapping when the kind changes — the property
                // namespace differs per kind so the old mapping is
                // certainly wrong.
                mapping: {},
              })
            }
          >
            <SelectTrigger id="downstream_kind" className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None — accepted, no record created</SelectItem>
              {SUBMISSION_DOWNSTREAM_KINDS.map((kind) => (
                <SelectItem key={kind} value={kind}>
                  {downstreamLabel(kind)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            On accept, build a record of this kind from the mapping below.
          </p>
        </div>

        <div className="flex items-start justify-between rounded-xl border bg-card p-4">
          <div className="min-w-0">
            <Label htmlFor="auto_accept">Auto-accept on submit</Label>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Skip the review queue and create the downstream record
              immediately. Used by the deal-registration form.
            </p>
          </div>
          <Switch
            id="auto_accept"
            checked={acceptance.auto_accept ?? false}
            onCheckedChange={(checked) => patchAcceptance({ auto_accept: checked })}
          />
        </div>
      </div>

      {acceptance.downstream_kind === "none" ||
      acceptance.downstream_kind === "partner_application" ? (
        <div className="rounded-xl border border-dashed bg-card/40 p-6 text-center text-sm text-muted-foreground">
          {acceptance.downstream_kind === "none"
            ? "No downstream record — accepted submissions just sit in the queue."
            : "Partner application downstream is not yet wired up in Phase A."}
        </div>
      ) : (
        <div className="rounded-xl border bg-card">
          <header className="border-b px-4 py-3">
            <h3 className="font-display text-sm font-semibold tracking-tight">
              Field mapping
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Map each form field to a property on the downstream{" "}
              <span className="font-medium">
                {downstreamLabel(acceptance.downstream_kind)}
              </span>{" "}
              record.
            </p>
          </header>

          {mappableFields.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">
              Add some fields in the Form builder tab first.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5">Form field</th>
                  <th className="px-4 py-2.5">Downstream property</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {mappableFields.map((f) => (
                  <tr key={f.name}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{f.label}</div>
                      <div className="font-mono text-[11px] text-muted-foreground">
                        {f.name}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Select
                        value={acceptance.mapping[f.name] ?? ""}
                        onValueChange={(v) =>
                          setMapping(f.name, v === "__unset" ? null : v)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Pick a property…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unset">— Unmapped —</SelectItem>
                          {downstreamOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Separator />
      <p className="text-[11px] text-muted-foreground">
        Tip: leave a field unmapped if it&apos;s for review-only context and
        shouldn&apos;t land on the downstream record.
      </p>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Property option resolution per downstream kind.
//
// 'deal' is special: Colab's deals table has a fixed column set. The
// submission accept flow inserts into public.deals, so the mapping target
// must be a public.deals column name, NOT a HubSpot property — HubSpot
// sync happens AFTER the deal row is created.
//
// All other HubSpot-object kinds use the shared HUBSPOT_PROPERTY_REGISTRY.
// ----------------------------------------------------------------------------

const DEAL_COLUMNS: { value: string; label: string }[] = [
  { value: "title", label: "Title" },
  { value: "deal_type", label: "Deal type" },
  { value: "prospect_company", label: "Prospect company" },
  { value: "prospect_contact_name", label: "Prospect contact name" },
  { value: "prospect_contact_email", label: "Prospect contact email" },
  { value: "estimated_value_eur", label: "Estimated value (EUR)" },
  { value: "notes", label: "Notes" },
];

function useDownstreamPropertyOptions(
  kind: FormWorkflowConfig["acceptance"]["downstream_kind"],
): { value: string; label: string }[] {
  switch (kind) {
    case "deal":
      return DEAL_COLUMNS;
    case "contact":
    case "company":
    case "lead":
      return fromHubSpotRegistry(kind);
    case "partner_application":
      return fromHubSpotRegistry("partner");
    case "none":
      return [];
  }
}

function fromHubSpotRegistry(object: HubSpotObject): { value: string; label: string }[] {
  return HUBSPOT_PROPERTY_REGISTRY[object].map((p) => ({
    value: p.property,
    label: `${p.label} (${p.property})`,
  }));
}

function downstreamLabel(
  kind: FormWorkflowConfig["acceptance"]["downstream_kind"],
): string {
  switch (kind) {
    case "deal":
      return "Deal";
    case "lead":
      return "Lead";
    case "partner_application":
      return "Partner application";
    case "contact":
      return "Contact";
    case "company":
      return "Company";
    case "none":
      return "None";
  }
}
